import { ElementType, useState } from "react";

import {
  Avatar,
  Drawer,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Typography as MUITypography,
  List as MUIList,
  ListItemButton as MUIListItemButton,
  Popover,
  Tooltip,
  Box,
  Collapse,
} from "@mui/material";
import avatarSrc from "assets/images/avatar.png";
import { Paths, SandboxUrlLoginRedirect, UseLoginLocal } from "configs";
import { shadows } from "configs/shadows";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { saveLocalStorage } from "utils";

import { useSidebar } from "./Sidebar.hooks";
import {
  Container,
  Logo,
  List,
  ListItemButton,
  SubList,
  LogoutButton,
  UserInfo,
  LogoWrapper,
  Divider,
  UserWrapper,
  Flag,
  MenuWrapper,
  MenuScrollbar,
  MenuContainer,
  MenuBarTrack,
  MenuBarPlaceholder,
} from "./Sidebar.styles";
import { SidebarMenuItem, SidebarProps } from "./Sidebar.types";
import { ChevronRightIcon, LogOut01Icon } from "../../atoms/Icon";
import Typography from "../../atoms/Typography";
import { OrgMenuItem } from "../OrgMenuItem";

const checkSubmenuActive = (submenu: SidebarMenuItem[]): boolean => {
  let active = false;
  for (const s of submenu) {
    if (s.path && window.location.pathname.includes(s.path)) {
      active = true;
      break;
    }
    if (Array.isArray(s.children) && s.children.length > 0) {
      active = checkSubmenuActive(s.children);
      if (active) break;
    }
  }
  return active;
};

const MenuRendering = (
  data: SidebarProps["menu"],
  level: number,
  collapsed: boolean
) => {
  const location = useLocation();

  const [menuId, setMenuId] = useState<string | null>(null);

  const onClickHandler = (id: string) => {
    setMenuId((prev) => (prev === id ? null : id));
  };

  return data.map(({ icon, id, path, title, children = [] }) => {
    const isChildrenEmpty = children.length === 0;
    const isOpen = menuId === id;
    const isHyperlink = !!path;
    let isActive = false;
    if (isHyperlink) {
      isActive =
        path === Paths.App
          ? location.pathname === Paths.App
          : location.pathname.includes(path);
    } else {
      isActive = checkSubmenuActive(children);
      if (isActive && children?.length && !menuId) {
        setMenuId(id);
      }
    }
    return (
      <Box key={id}>
        <ListItemButton
          to={isHyperlink ? path : undefined}
          component={isHyperlink ? RouterLink : undefined}
          onClick={() => (!isChildrenEmpty ? onClickHandler(id) : null)}
          level={level}
          open={isOpen}
          collapsed={collapsed}
          active={isActive}
        >
          <ListItemIcon>
            <SvgIcon
              fontSize="small"
              component={icon as unknown as ElementType}
            />
          </ListItemIcon>
          <ListItemText primary={title} />
          {!isChildrenEmpty ? <ChevronRightIcon /> : null}
        </ListItemButton>
        {!isChildrenEmpty ? (
          <Collapse in={!collapsed ? isOpen : false}>
            <SubList level={level} numberOfChildren={children?.length ?? 0}>
              {MenuRendering(children, level + 1, collapsed)}
            </SubList>
          </Collapse>
        ) : null}
      </Box>
    );
  });
};

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  menu,
  bottomMenu,
  userInfo,
  languages,
  defaultLanguage,
  onHover,
  onBlur,
  onLogout,
  onLanguageChange,
}) => {
  const { language } = useSidebar({
    defaultLanguage,
    languages,
    onBlur,
    onLanguageChange,
  });

  const currentLanguage = languages?.find((i) => i.id === language.value);

  return (
    <Container
      collapsed={collapsed}
      onMouseEnter={onHover}
      onMouseLeave={onBlur}
    >
      <Drawer
        open
        variant="persistent"
        anchor="left"
        PaperProps={{
          sx: { overflowX: "hidden" },
        }}
      >
        <Stack
          justifyContent="space-between"
          height="100%"
          gap={4}
          paddingY={4}
        >
          <LogoWrapper
            href={UseLoginLocal ? Paths.App : SandboxUrlLoginRedirect}
          >
            <Logo collapsed={collapsed} />
          </LogoWrapper>
          <Divider />
          <OrgMenuItem collapsed={collapsed} />
          <Divider />
          <MenuContainer className="menu-container">
            <MenuWrapper className="menu-wrapper">
              <List className="menu-list" component="nav">
                {MenuRendering(menu, 1, collapsed)}
              </List>
              <List component="nav">
                {currentLanguage ? (
                  <ListItemButton
                    level={1}
                    collapsed={collapsed}
                    onClick={language.open}
                  >
                    <Flag
                      src={currentLanguage.flagIcon}
                      alt={currentLanguage.name}
                    />
                    <ListItemText primary={currentLanguage.name} />
                  </ListItemButton>
                ) : null}
                {MenuRendering(bottomMenu, 1, collapsed)}
              </List>
            </MenuWrapper>
            <MenuBarTrack className="menu-bar-track">
              <MenuBarPlaceholder className="menu-bar-placeholder" />
              <MenuScrollbar className="menu-scrollbar" />
            </MenuBarTrack>
          </MenuContainer>
          {userInfo ? (
            <Stack gap={4} flex="0 0 auto">
              <Divider />
              <UserWrapper>
                <Avatar
                  src={avatarSrc}
                  sx={{ marginLeft: "12px", flexBasis: "40px" }}
                />
                <UserInfo collapsed={collapsed}>
                  <Tooltip arrow title={userInfo.name}>
                    <MUITypography fontWeight={600}>
                      {userInfo.name}
                    </MUITypography>
                  </Tooltip>
                  <Tooltip arrow title={userInfo.email}>
                    <MUITypography>{userInfo.email}</MUITypography>
                  </Tooltip>
                </UserInfo>
                <LogoutButton collapsed={collapsed} onClick={onLogout}>
                  <LogOut01Icon fontSize="small" color="inherit" />
                </LogoutButton>
              </UserWrapper>
            </Stack>
          ) : null}
        </Stack>
      </Drawer>
      {languages && languages?.length > 0 ? (
        <Popover
          open={!!language.ref}
          anchorEl={language.ref}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { boxShadow: shadows.xs } } }}
          onClose={language.close}
        >
          <MUIList>
            {languages.map((i) => (
              <MUIListItemButton
                key={i.id}
                onClick={() => {
                  language.change(i.id);
                  saveLocalStorage("lan", i.id);
                  window.location.reload();
                }}
              >
                <Stack width="100%" direction="row" alignItems="center" gap={2}>
                  <Flag src={i.flagIcon} alt={i.name} />
                  <Typography type="TextSM" fontWeight={500} flex={1}>
                    {i.name}
                  </Typography>
                </Stack>
              </MUIListItemButton>
            ))}
          </MUIList>
        </Popover>
      ) : null}
    </Container>
  );
};

export default Sidebar;
